/**
 * Translates the visual config into the acquisition form's CSS variables. Every color
 * role falls back to a derivation when not configured: highlight background
 * comes from the primary mixed with white (SecondaryColorOpacity, default
 * 10%), button and selection border inherit the primary, and the CTA text
 * flips between light and dark based on the button color's luminance.
 */
export function buildThemeVars(visual) {
  if (!visual) return {};

  const primary = visual.PrimaryColor || '#3D52D5';
  const button = visual.ButtonColor || primary;
  const text = visual.TextColor || '#111827';
  const secondary = visual.SecondaryColor || tintHex(primary, visual.SecondaryColorOpacity || 10);

  return {
    '--tw-primary':       primary,
    '--tw-secondary':     secondary,
    '--tw-segment-bg':    tintHex(primary, 7),
    '--tw-text':          text,
    '--tw-border-accent': visual.BorderColor || primary,
    '--tw-cta':           button,
    '--tw-cta-dark':      darkenHex(button, 0.15),
    '--tw-cta-text':      isLightHex(button) ? text : '#FFFFFF',
    '--tw-font':          visual.FontFamily || 'Inter, sans-serif',
    fontFamily:           visual.FontFamily || 'Inter, sans-serif'
  };
}

function parseHex(hex) {
  if (typeof hex !== 'string') return null;
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const num = parseInt(match[1], 16);
  return { r: num >> 16, g: (num >> 8) & 0xff, b: num & 0xff };
}

function rgbToHex(r, g, b) {
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function tintHex(hex, opacityPercent) {
  const rgb = parseHex(hex);
  if (!rgb) return '#EEF1FC';
  const ratio = Math.min(Math.max(opacityPercent, 1), 100) / 100;
  const mix = channel => Math.round(channel * ratio + 255 * (1 - ratio));
  return rgbToHex(mix(rgb.r), mix(rgb.g), mix(rgb.b));
}

function darkenHex(hex, amount) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const darken = channel => Math.max(0, channel - Math.round(255 * amount));
  return rgbToHex(darken(rgb.r), darken(rgb.g), darken(rgb.b));
}

function isLightHex(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return false;
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return luminance > 0.62;
}
